package pl.zar.tikthrow.shared.dto

import kotlin.time.Instant
import kotlinx.serialization.Serializable
import pl.zar.tikthrow.application.PostStatus
import pl.zar.tikthrow.application.StoredPublication

/**
 * The name of the mandatory `POST /api/v1/publications` header (card #14 /
 * FR-010) — the one definition the server and the client share (card #88
 * review: the client used to duplicate the server's constant). 1–128
 * characters of `[A-Za-z0-9-_.]`. One (user, key) maps to exactly one
 * submission: the same key + the same payload (the same `media_key` + the
 * same `media_sha256`) replays the stored resource; a different payload is
 * a 409 `idempotency_key_conflict`.
 */
const val IDEMPOTENCY_KEY_HEADER = "Idempotency-Key"

/**
 * `POST /api/v1/publications` request body (card #14).
 *
 * The request must also carry the mandatory `Idempotency-Key` header — the body
 * alone is never enough to publish.
 *
 * @property platform target platform wire name; only `tiktok` is wired today.
 * @property caption post text (the platform applies its own limit).
 * @property privacyLevel platform privacy level; null = platform default
 *   (for TikTok: `SELF_ONLY`, the only level unaudited apps may use).
 * @property accountId the connected account id; null = the user's first account.
 * @property mediaBase64 the video, base64-encoded (TikTok requires a video).
 *   The legacy channel: with the R2 media backend
 *   (`TIKTHROW_MEDIA_BACKEND=r2`, feature 003-r2-media-storage) this field
 *   is rejected with 400 `use_presigned_upload` — the media bytes go to the
 *   bucket directly through the presigned flow and must not pass through
 *   the server.
 * @property mediaKey the media object's storage key (card #87 / T805) —
 *   the object the client **already uploaded** through the two-phase
 *   (presigned) flow (card #85). The row keeps the key (and [mediaSha256]
 *   when sent) instead of the bytes; the publish/review path fetches the
 *   object through the storage seam at publish time. Must be the caller's
 *   own key (`api/<user_id>/…`) and is mutually exclusive with
 *   [mediaBase64] — exactly one of the two must be present.
 * @property mediaSha256 the SHA-256 of the [mediaKey] object's content
 *   (card #87 / T805, FR-5): lowercase hex, 64 characters. Optional — when
 *   absent the row keeps a NULL hash; the server never re-reads the object
 *   at submission time to compute it (the bytes never pass through the
 *   server, spec C1).
 */
@Serializable
data class CreatePublicationRequest(
    val platform: String,
    val caption: String = "",
    val privacyLevel: String? = null,
    val accountId: String? = null,
    val mediaBase64: String? = null,
    /** The media object's storage key (wire name `media_key`, card #87). */
    @kotlinx.serialization.SerialName("media_key")
    val mediaKey: String? = null,
    /** The media object's SHA-256 (wire name `media_sha256`, card #87). */
    @kotlinx.serialization.SerialName("media_sha256")
    val mediaSha256: String? = null,
    /**
     * When to publish **after the submission is approved** (card #15) — by
     * a human when the review gate is on (every human session; a key with
     * `require_review`), or by the system at submission time when the key's
     * gate is off (T405 / spec C3) — as epoch millis. Absent/null = publish
     * immediately upon approval. Must be a time in the future when present
     * (400 otherwise).
     */
    val scheduledAtMillis: Long? = null,
)

/**
 * `POST /api/v1/publications/{id}/reject` body (card #15).
 *
 * A human (app session) rejects a pending submission. [reason] is optional —
 * it is recorded on the row for the audit (T10) and may be empty.
 */
@Serializable
data class RejectPublicationRequest(
    val reason: String? = null,
)

/**
 * The stored publication resource (card #14 / FR-011, extended by card #15)
 * — what happened and **who** did it. `POST /api/v1/publications`
 * (submission), the approve/reject endpoints and
 * `GET /api/v1/publications/{id}` return this object; a replayed
 * `Idempotency-Key` returns the very same resource.
 *
 * @property actorType `HUMAN` = the owner's app session, `AGENT` = an API
 *   token — the one that **submitted** the publication.
 * @property apiTokenId the `api_tokens` row id that submitted; null for humans.
 * @property platform the target platform.
 * @property externalPostId the platform's own post id (null until PUBLISHED).
 * @property status the unified state: `PENDING_APPROVAL`, `APPROVED`,
 *   `SCHEDULED`, `PUBLISHING`, `PUBLISHED`, `REJECTED`, `FAILED`.
 * @property scheduledAt the approved time to publish (null = immediately
 *   upon approval).
 * @property approvedByUserId the user who approved (a human app session) —
 *   the card #15 audit (T10): the audit shows a human approved it.
 * @property approvedAt when the human approval happened.
 * @property rejectedByUserId the user who rejected (a human app session).
 * @property rejectedAt when the human rejection happened.
 * @property rejectReason the human's rejection reason, when given.
 * @property publishedAt set when the platform confirmed the post.
 * @property errorMessage the structural failure reason (null on success).
 */
@Serializable
data class PublicationDto(
    val id: String,
    val idempotencyKey: String,
    val userId: String,
    val actorType: String,
    val apiTokenId: String?,
    val platform: String,
    val externalPostId: String?,
    val status: String,
    val scheduledAt: Instant?,
    val approvedByUserId: String?,
    val approvedAt: Instant?,
    val rejectedByUserId: String?,
    val rejectedAt: Instant?,
    val rejectReason: String?,
    val publishedAt: Instant?,
    val errorMessage: String?,
    val createdAt: Instant,
) {
    companion object {
        fun from(row: StoredPublication): PublicationDto = PublicationDto(
            id = row.id,
            idempotencyKey = row.idempotencyKey,
            userId = row.ownerId,
            actorType = row.actor.type.name,
            apiTokenId = row.actor.apiTokenId,
            platform = row.platform,
            externalPostId = row.externalPostId,
            status = row.status.name,
            scheduledAt = row.scheduledAt,
            approvedByUserId = row.approvedByUserId,
            approvedAt = row.approvedAt,
            rejectedByUserId = row.rejectedByUserId,
            rejectedAt = row.rejectedAt,
            rejectReason = row.rejectReason,
            publishedAt = row.publishedAt,
            errorMessage = row.errorMessage,
            createdAt = row.createdAt,
        )
    }
}

/**
 * `GET /api/v1/stats` response (card #41 / T701): the owner's own
 * publication statistics — how many published / scheduled / failed per
 * channel (platform).
 *
 * The counts match the `/api/v1/publications` listing 1:1 (card #41
 * acceptance). A channel appears only when it has at least one publication
 * in one of the three counted states.
 */
@Serializable
data class ChannelStats(
    /** The channel (platform) wire name, e.g. `tiktok`. */
    val platform: String,
    /** The number of [PostStatus.PUBLISHED] publications on this channel. */
    val published: Int,
    /** The number of [PostStatus.SCHEDULED] publications on this channel. */
    val scheduled: Int,
    /** The number of [PostStatus.FAILED] publications on this channel. */
    val failed: Int,
)

/**
 * `GET /api/v1/stats` response body (card #41 / T701): the list of per-channel
 * statistics.
 */
@Serializable
data class StatsResponse(
    val channels: List<ChannelStats>,
)
